import { useState, useEffect, useRef } from 'react';
//import cotastatsimg from '../shared/assets/charactersheets/cotastats.png';
//import cotalogo from '../shared/assets/logos/cotalogo.png';
//import cotatitle from '../shared/assets/logos/cotatitle.png';
import { useNavigate } from 'react-router-dom';
import '../shared/styles/Page.css';
import { Button } from '../shared/components/Button';
import dfLogo from '../shared/assets/logos/dimensionfactory.png';
import SideDrawer from '../shared/components/SideDrawer';
import {
  Sun,
  Moon,
  Plus,
  Square,
  Armchair,
  Database,
  Cog,
  LogOut,
  Pencil,
  Fullscreen,
} from 'lucide-react';

const LOGOUT_CONFIRM_CLICKS = 3;

export default function TopBar({
  mode,
  setMode,
  scenes = [],
  currentSceneId,
  onSelectScene,
  onAddScene,
  onEditScenes,
  isOnBreak = false,
  onToggleBreak,
  onAssetManagement,
  onDisplayAction,
  managedDisplayIsOpen,
}) {
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [scenesOpen, setScenesOpen] = useState(false);
  const [logoutClicksLeft, setLogoutClicksLeft] = useState(LOGOUT_CONFIRM_CLICKS);
  const displayWindowRef = useRef(null);
  const [localDisplayIsOpen, setLocalDisplayIsOpen] = useState(false);
  const isDisplayOpen = managedDisplayIsOpen ?? localDisplayIsOpen;
  const system =
    typeof window !== 'undefined' ? localStorage.getItem('system') : null;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Keep drawers from both being open at once
    if (settingsOpen && scenesOpen) setScenesOpen(false);
  }, [settingsOpen, scenesOpen]);

  const syncDisplayWindowState = () => {
    const w = displayWindowRef.current;
    setLocalDisplayIsOpen(!!(w && !w.closed));
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (onDisplayAction) return;
    if (system !== 'orchestrator') return;

    const intervalId = window.setInterval(syncDisplayWindowState, 750);
    return () => window.clearInterval(intervalId);
  }, [onDisplayAction, system]);

  const openOrRefreshDisplay = () => {
    if (typeof window === 'undefined') return;

    const w = displayWindowRef.current;
    if (w && !w.closed) {
      try {
        w.location.reload();
        w.focus();
      } catch {
        // If reload is blocked for any reason, at least focus the window
        try {
          w.focus();
        } catch {}
      }
      syncDisplayWindowState();
      return;
    }

    const newWin = window.open(
      '/orchestrator/display',
      'displayWindow',
      'width=1400,height=800',
    );

    displayWindowRef.current = newWin;
    syncDisplayWindowState();
  };

  const handleDisplayAction = onDisplayAction || openOrRefreshDisplay;

  const doLogout = () => {
    localStorage.removeItem('username');
    localStorage.removeItem('system');
    localStorage.removeItem('campaign');
    setTimeout(() => {
      navigate('/');
    }, '200');
  };

  const handleLogoutClick = () => {
    if (logoutClicksLeft <= 1) {
      setLogoutClicksLeft(LOGOUT_CONFIRM_CLICKS);
      doLogout();
      return;
    }
    setLogoutClicksLeft((previous) => previous - 1);
  };

  const logoutDescription = logoutClicksLeft < LOGOUT_CONFIRM_CLICKS
    ? `Press ${logoutClicksLeft} more time${logoutClicksLeft !== 1 ? 's' : ''} to Logout`
    : '';

  return (
    <div style={{ width: '100%' }}>
      <SideDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        side="right"
        title="Settings"
        mode={mode}
      >
        <Button
          icon={mode === 'dark' ? Sun : Moon}
          label={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
          type={mode === 'dark' ? 'onyx' : 'cinnabar'}
          onPress={() => setMode(mode === 'dark' ? 'light' : 'dark')}
        />

        <Button
          icon={Fullscreen}
          label={isDisplayOpen ? 'Refresh Display' : 'Open Display'}
          type={mode === 'dark' ? 'onyx' : 'cinnabar'}
          onPress={() => {
            handleDisplayAction();
          }}
        />

        <Button
          icon={Database}
          label={'Asset Management'}
          type={mode === 'dark' ? 'onyx' : 'cinnabar'}
          onPress={() => {
            if (onAssetManagement) onAssetManagement();
            setSettingsOpen(false);
          }}
        />

        <Button
          icon={Armchair}
          type={
            isOnBreak
              ? mode === 'light'
                ? 'tomato'
                : 'cinnabar'
              : mode === 'dark'
                ? 'onyx'
                : 'cinnabar'
          }
          label={isOnBreak ? 'Come back from Break' : 'Take a Break'}
          onPress={() => {
            if (onToggleBreak) onToggleBreak();
          }}
        />

        <Button
          icon={LogOut}
          label={'Logout'}
          description={logoutDescription}
          type={mode === 'dark' ? 'onyx' : 'cinnabar'}
          onPress={() => {
            handleLogoutClick();
          }}
        />

      </SideDrawer>

      <SideDrawer
        open={scenesOpen}
        onClose={() => setScenesOpen(false)}
        side="left"
        title="Scenes"
        mode={mode}
      >
        {scenes.map((s) => (
          <Button
            key={s.id}
            label={s.name}
            type={
              s.id === currentSceneId
                ? mode === 'light'
                  ? 'tomato'
                  : 'ivory'
                : mode === 'dark'
                  ? 'onyx'
                  : 'cinnabar'
            }
            onPress={() => {
              if (onSelectScene) onSelectScene(s.id);
              setScenesOpen(false);
            }}
          />
        ))}
        <Button
          icon={Plus}
          label={'Add scene'}
          type={mode === 'dark' ? 'onyx' : 'cinnabar'}
          onPress={() => {
            if (onAddScene) onAddScene();
            setScenesOpen(false);
          }}
        />
        <Button
          icon={Pencil}
          label={'Edit Screens'}
          type={mode === 'dark' ? 'onyx' : 'cinnabar'}
          onPress={() => {
            if (onEditScenes) onEditScenes();
            setScenesOpen(false);
          }}
        />
      </SideDrawer>

      <img
        style={{
          top: '5px',
          left: '10px',
          height: '70px',
          position: 'absolute',
        }}
        alt=""
        src={dfLogo}
      />

      <div className="topbar_action_group">
        {system !== 'orchestrator' && (
          <Button
            icon={mode === 'dark' ? Sun : Moon}
            type={mode === 'dark' ? 'onyx' : 'cinnabar'}
            description={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
            onPress={() => setMode(mode === 'dark' ? 'light' : 'dark')}
          />
        )}

        {system === 'orchestrator' && windowWidth >= 775 && (
          <>
            <Button
              icon={Fullscreen}
              type={mode === 'dark' ? 'onyx' : 'cinnabar'}
              description={isDisplayOpen ? 'Refresh Display' : 'Open Display'}
              onPress={() => {
                handleDisplayAction();
              }}
            />

            <Button
              icon={Database}
              description={'Asset Management'}
              type={mode === 'dark' ? 'onyx' : 'cinnabar'}
              onPress={() => {
                if (onAssetManagement) onAssetManagement();
              }}
            />

            <Button
              icon={mode === 'dark' ? Sun : Moon}
              type={mode === 'dark' ? 'onyx' : 'cinnabar'}
              description={mode === 'dark' ? 'Light Mode' : 'Dark Mode'}
              onPress={() => setMode(mode === 'dark' ? 'light' : 'dark')}
            />

            <Button
              icon={Armchair}
              type={
                isOnBreak
                  ? mode === 'light'
                    ? 'ivory'
                    : 'cinnabar'
                  : mode === 'dark'
                    ? 'onyx'
                    : 'cinnabar'
              }
              description={isOnBreak ? 'Come back from Break' : 'Take a Break'}
              onPress={() => {
                if (onToggleBreak) onToggleBreak();
              }}
            />
          </>
        )}

        {system === 'orchestrator' && windowWidth < 775 && (
          <Button
            icon={Cog}
            type={mode === 'dark' ? 'onyx' : 'cinnabar'}
            description={'Settings'}
            onPress={() => {
              setSettingsOpen(true);
            }}
          />
        )}

        {!(system === 'orchestrator' && windowWidth < 775) && (
          <Button
            icon={LogOut}
            description={logoutDescription || 'Logout'}
            type={mode === 'dark' ? 'onyx' : 'cinnabar'}
            onPress={() => {
              handleLogoutClick();
            }}
          />
        )}
      </div>

      {system === 'orchestrator' && (
        <>
          {/* Scenes controls */}
          <div className="topbar_scene_scroller">
            {windowWidth >= 1205 && (
              <>
                {scenes.map((s) => (
                  <Button
                    key={s.id}
                    label={s.name}
                    type={
                      s.id === currentSceneId
                        ? 'ivory'
                        : mode === 'dark'
                          ? 'onyx'
                          : 'cinnabar'
                    }
                    onPress={() => onSelectScene && onSelectScene(s.id)}
                  />
                ))}
                <Button
                  icon={Plus}
                  type={mode === 'dark' ? 'onyx' : 'cinnabar'}
                  description={'Add Scene'}
                  onPress={() => onAddScene && onAddScene()}
                />
                <Button
                  icon={Pencil}
                  type={mode === 'dark' ? 'onyx' : 'cinnabar'}
                  description={'Edit Screens'}
                  onPress={() => onEditScenes && onEditScenes()}
                />
              </>
            )}
            {windowWidth < 1205 && (
              <>
                {currentSceneId &&
                  scenes.length > 0 &&
                  (() => {
                    const currentScene = scenes.find(
                      (s) => s.id === currentSceneId,
                    );
                    return currentScene ? (
                      <Button
                        icon={Square}
                        label={currentScene.name}
                        description="Screen Management"
                        type={mode === 'light' ? 'tomato' : 'ivory'}
                        onPress={() => setScenesOpen(true)}
                      />
                    ) : null;
                  })()}
                {!currentSceneId && scenes.length === 0 && (
                  <Button
                    icon={Plus}
                    description="Add Scene"
                    type={mode === 'dark' ? 'onyx' : 'cinnabar'}
                    onPress={() => onAddScene && onAddScene()}
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
